import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

import logo from "@/assets/ascflow.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownHover, setIsDropdownHover] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    }
  }, [location]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsDropdownHover(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsDropdownHover(false), 150);
  };

  // ✅ Ajuste fino aqui:
  // se "foi pro A" => diminui (ex: 30)
  // se o símbolo azul invadir o texto => aumenta (ex: 34)
  const SYMBOL_CUT = 37; // %

  const SYMBOL_BLUE = "#1B75D0";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border font-sans">
      <nav className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* LOGO ÚNICA (símbolo azul + texto branco) */}
          <Link to="/" className="flex items-center">
            <span className="relative inline-block" style={{ width: "133px", height: "56px" }}>
              {/* Base invisível só pra garantir o tamanho real */}
              <img src={logo} alt="" className="h-14 w-auto opacity-0" width="133" height="56" />

              {/* Texto branco (remove o símbolo do lado esquerdo) */}
              <img
                src={logo}
                alt="AscFlow"
                className="absolute inset-0 h-14 w-auto brightness-0 invert"
                style={{
                  clipPath: `inset(0 0 0 ${SYMBOL_CUT}%)`,
                }}
                width="133"
                height="56"
              />

              {/* Símbolo azul (mostra só a parte esquerda) */}
              <span
                aria-hidden="true"
                className="absolute inset-0 h-14 w-auto pointer-events-none"
                style={{
                  backgroundColor: SYMBOL_BLUE,
                  WebkitMaskImage: `url(${logo})`,
                  maskImage: `url(${logo})`,
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskPosition: "left center",
                  maskPosition: "left center",
                  clipPath: `inset(0 ${100 - SYMBOL_CUT}% 0 0)`,
                }}
              />
            </span>
          </Link>

          {/* --- MENU DESKTOP --- */}
          <div className="hidden md:flex items-center space-x-8 text-[15px] font-medium capitalize relative">
            <a
              href="/#integracoes"
              className="text-foreground hover:text-[#7C3AED] transition-colors duration-300"
            >
              Integrações
            </a>
            <a
              href="/#automacao"
              className="text-foreground hover:text-[#7C3AED] transition-colors duration-300"
            >
              Automação
            </a>
            <a
              href="/#seguranca"
              className="text-foreground hover:text-[#7C3AED] transition-colors duration-300"
            >
              Segurança
            </a>
            <a
              href="/#planos"
              className="text-foreground hover:text-[#7C3AED] transition-colors duration-300"
            >
              Planos
            </a>

            {/* --- DROPDOWN: Casos de Uso --- */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center gap-1 text-foreground hover:text-[#7C3AED] transition-colors duration-300"
                aria-label="Casos de uso"
                aria-expanded={isDropdownHover}
                aria-haspopup="true"
              >
                Casos de uso
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-300 ${isDropdownHover ? "rotate-180 text-[#7C3AED]" : ""
                    }`}
                  aria-hidden="true"
                />
              </button>

              {isDropdownHover && (
                <div className="absolute top-8 left-0 mt-3 w-56 bg-background/95 backdrop-blur-md border border-[#7C3AED30] rounded-xl shadow-[0_0_18px_rgba(124,58,237,0.2)] p-3 flex flex-col gap-1 animate-fade-in">
                  <Link
                    to="/usecases/cobranca"
                    className="text-sm text-foreground hover:text-[#7C3AED] hover:bg-[#7C3AED10] rounded-md px-3 py-2 transition-all"
                  >
                    Cobranças
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/documentation"
              className="text-foreground hover:text-[#7C3AED] transition-colors duration-300"
            >
              Documentação
            </Link>


          </div>

          {/* --- BOTÃO LOGIN --- */}
          <div className="hidden md:flex items-center">
            <Button
              asChild
              className="bg-[#1B75D0] text-white font-semibold tracking-wide rounded-full px-6 py-2 text-[15px] transition-all duration-500 shadow-[0_0_14px_rgba(27,117,208,0.45)] hover:shadow-[0_0_25px_rgba(27,117,208,0.75)] hover:brightness-110"
            >
              <a
                href="https://flow.ascconecta.com.br/login"
                target="_blank"
                rel="noopener noreferrer"
              >
                Login
              </a>
            </Button>
          </div>

          {/* --- BOTÃO MOBILE --- */}
          <button
            className="md:hidden text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-haspopup="dialog"
          >
            {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* --- MENU MOBILE --- */}
        {isOpen && (
          <div
            id="mobile-menu"
            className="md:hidden py-4 space-y-4 border-t border-border text-center font-sans capitalize text-[15px]"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
          >
            <a
              href="/#integracoes"
              className="block text-foreground hover:text-[#7C3AED] transition-colors duration-300"
              onClick={() => setIsOpen(false)}
            >
              Integrações
            </a>
            <a
              href="/#automacao"
              className="block text-foreground hover:text-[#7C3AED] transition-colors duration-300"
              onClick={() => setIsOpen(false)}
            >
              Automação
            </a>
            <a
              href="/#seguranca"
              className="block text-foreground hover:text-[#7C3AED] transition-colors duration-300"
              onClick={() => setIsOpen(false)}
            >
              Segurança
            </a>
            <a
              href="/#planos"
              className="block text-foreground hover:text-[#7C3AED] transition-colors duration-300"
              onClick={() => setIsOpen(false)}
            >
              Planos
            </a>

            <details className="px-4">
              <summary className="cursor-pointer text-foreground hover:text-[#7C3AED] transition-colors duration-300">
                Casos de uso
              </summary>
              <div className="flex flex-col mt-2 gap-2 text-sm">
                <Link
                  to="/usecases/cobranca"
                  className="text-muted-foreground hover:text-[#7C3AED]"
                  onClick={() => setIsOpen(false)}
                >
                  Cobranças via WhatsApp
                </Link>
              </div>
            </details>

            <Link
              to="/documentation"
              className="block text-foreground hover:text-[#7C3AED] transition-colors duration-300"
              onClick={() => setIsOpen(false)}
            >
              Documentação
            </Link>



            <div className="pt-4 flex justify-center">
              <Button
                className="bg-[#1B75D0] text-white font-semibold tracking-wide rounded-full px-6 py-2 text-[15px] transition-all duration-500 shadow-[0_0_14px_rgba(27,117,208,0.45)] hover:shadow-[0_0_25px_rgba(27,117,208,0.75)] hover:brightness-110"
                asChild
              >
                <a
                  href="https://flow.ascconecta.com.br/login"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Login
                </a>
              </Button>
            </div>
          </div>
        )}
      </nav>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.25s ease-out forwards;
        }
      `}</style>
    </header>
  );
};

export default Navbar;
